import { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import _intersection from 'lodash.intersection';
import { useAddUserRole } from '../../../services/mutations';
import { AddUserRoleForm } from './form';
import { isManagerRole, isOperationSustainability } from '../../../common/roles';

function DialogFooter({ onCancel = () => {}, onSubmit = () => {}, disabled = true, submitting = false }) {
  return (
    <div>
      <Button label="Cancel" onClick={onCancel} size="small" text />
      <Button label="Add role" loading={submitting} onClick={onSubmit} size="small" disabled={disabled} autoFocus />
    </div>
  );
}

function AddUserRoleModal({ userId = '', open = false, userRolesInfo = null, onClose = () => {} }) {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const formRef = useRef<HTMLFormElement>(null);
  const addUserRole = useAddUserRole();

  const closeModal = () => {
    onClose();
    formRef.current?.reset();
    addUserRole.reset();
  };

  const submit = () => formRef.current?.requestSubmit();

  const handleSubmit = (data: any) => {
    setConfirmLoading(true);

    addUserRole.mutate(
      // { RoleIds: data.roles.map((r: any) => r.id), UserId: Number(userId) },
      { RoleIds: [data.roles.id], UserId: Number(userId) },
      {
        onSuccess: () => closeModal(),
        onSettled: () => setConfirmLoading(false),
      },
    );
  };

  const handleCancel = () => {
    setSelectedRole(null);
    closeModal();
  };

  const hasRoleCollision = () => {
    if (selectedRole) {
      if (isManagerRole(selectedRole.grant)) {
        return _intersection(userRolesInfo.isOperationSustIn, selectedRole.placeCodes).length > 0;
      }

      if (isOperationSustainability(selectedRole.grant)) {
        return _intersection(userRolesInfo.isManagerIn, selectedRole.placeCodes).length > 0;
      }
    }
    return false;
  };

  return (
    <Dialog
      header="Add new user role"
      onHide={handleCancel}
      footer={
        <DialogFooter
          onCancel={handleCancel}
          onSubmit={submit}
          disabled={!selectedRole || hasRoleCollision()}
          submitting={confirmLoading}
        />
      }
      visible={open}
      breakpoints={{ '600px': '100vw' }}
      style={{ width: '450px' }}
      modal
    >
      <AddUserRoleForm onSubmit={handleSubmit} onSelectionChange={(role: any) => setSelectedRole(role)} ref={formRef} />
      {addUserRole.isError && <Message severity="error" text="Unable to add user role" />}
      {hasRoleCollision() && (
        <Message
          severity="error"
          text="It is not allowed to assign manager role (EE Manager, Mill Manager) and Operational Sustainability at the same time to the same location."
        />
      )}
    </Dialog>
  );
}

export default AddUserRoleModal;
